/**
 * Полосы тарифа: хранимый интервал вместо синтезированной подписи.
 *
 * Экран `/rates` исторически знал только НИЖНЮЮ границу полосы, а верхнюю рисовал по
 * соседней строке. Пока сетка непрерывна, это совпадает с хранимым `property_rate`, но
 * перекрывающиеся полосы такая модель показать не может: две полосы с одной нижней
 * границей склеивались в одну строку, и цена второй исчезала с экрана, оставаясь в базе
 * (задача 10577). Здесь интервал — первичный ключ строки, а подпись выводится из него.
 *
 * Верхняя граница `null` = «и выше» (в базе `rate_to = -1`). Совпадение полуинтервалом
 * `[from, to)` — как в `RateBandCodec::sqlPercentInBand` расчёта.
 */

export type BandBound = number | null;

export interface Band {
  from: number;
  to: BandBound;
}

export interface BandRange {
  from: number;
  to: BandBound;
}

export interface BandRelation {
  rate_id: number;
  from: number | null;
  to: BandBound;
  ranges: Array<BandRange>;
}

/** Вердикт по полосе из ответа `/tariff-get` (считает work-calc, фронт только красит). */
export interface BandVerdict {
  rate_id: number;
  from: number;
  to: BandBound;
  applies: 'full' | 'partial' | 'none';
  severity: 'clean' | 'partial' | 'dead' | 'gap';
  ranges: Array<BandRange>;
  shadowed_by: Array<BandRelation>;
  shadows: Array<BandRelation>;
}

export const OPEN_TOP = Infinity;

/** Ключ строки/столбца: интервал, а не нижняя граница. */
export function bandKey(band: Band): string {
  return `${band.from}|${band.to === null ? '' : band.to}`;
}

export function upperBound(band: Band): number {
  return band.to === null ? OPEN_TOP : band.to;
}

/** Число без хвостовых нулей: 80.14 → «80,14», 100 → «100». */
export function formatBound(value: number): string {
  return String(Math.round(value * 100) / 100).replace('.', ',');
}

/**
 * Подпись строки. Порядок «сверху вниз», как на экране исторически: `90 - 0` для
 * хранимого `0 – 90`, «100» для полосы без верха. У непрерывной сетки цифры те же, что
 * до задачи 10577, — экран 1486 здоровых видов работ не меняется.
 */
export function bandLabel(band: Band): string {
  if (band.to === null) {
    return formatBound(band.from);
  }
  return `${formatBound(band.to)} - ${formatBound(band.from)}`;
}

/** Подпись для тултипа/сводки — читается без знания порядка столбцов. */
export function bandTitle(band: Band): string {
  if (band.to === null) {
    return `от ${formatBound(band.from)} % и выше`;
  }
  return `от ${formatBound(band.from)} до ${formatBound(band.to)} %`;
}

export function rangeTitle(range: BandRange): string {
  if (range.to === null) {
    return `${formatBound(range.from)} % и выше`;
  }
  return `${formatBound(range.from)}–${formatBound(range.to)} %`;
}

export function rangesTitle(ranges: Array<BandRange>): string {
  return ranges.map(rangeTitle).join(', ');
}

/**
 * Порядок строк: сверху большие проценты. При одной нижней границе узкая полоса идёт
 * раньше широкой — тем же правилом, что и `Get.php` (открытый верх последним). Иначе
 * какая из двух цен окажется первой, решал бы порядок в ответе.
 */
export function sortBandsForDisplay(bands: Array<Band>): Array<Band> {
  return [...bands].sort((a, b) => {
    if (a.from !== b.from) {
      return b.from - a.from;
    }
    return upperBound(a) - upperBound(b);
  });
}

export function sortBandsAscending(bands: Array<Band>): Array<Band> {
  return [...bands].sort((a, b) => {
    if (a.from !== b.from) {
      return a.from - b.from;
    }
    return upperBound(a) - upperBound(b);
  });
}

/** Полосы, в которые попадает процент (полуинтервал `[from, to)`). */
export function bandsAt(bands: Array<Band>, percent: number): Array<Band> {
  return bands.filter((band) => band.from <= percent && percent < upperBound(band));
}

export function rangesContain(ranges: Array<BandRange>, percent: number): boolean {
  return ranges.some((range) => range.from <= percent && percent < (range.to === null ? OPEN_TOP : range.to));
}

/**
 * Проценты от 0 до бесконечности, не покрытые ни одной полосой. Дырка не даёт ошибки:
 * расчёт пишет `COALESCE(…, 0)`, то есть в закрытие уходит ноль (`AssembleRepository`).
 */
export function coverageGaps(bands: Array<Band>): Array<BandRange> {
  if (!bands.length) {
    return [{ from: 0, to: null }];
  }
  const sorted = sortBandsAscending(bands);
  const gaps: Array<BandRange> = [];
  let cursor = 0;

  sorted.forEach((band) => {
    if (band.from > cursor) {
      gaps.push({ from: cursor, to: band.from });
    }
    cursor = Math.max(cursor, upperBound(band));
  });

  if (cursor !== OPEN_TOP) {
    gaps.push({ from: cursor, to: null });
  }

  return gaps;
}

/** Полоса, стоящая ровно под этой (её верх = наш низ) — для сдвига и удаления границы. */
export function neighbourBelow(bands: Array<Band>, band: Band): Band | null {
  const candidates = bands.filter((other) => other.to !== null && other.to === band.from);
  if (candidates.length !== 1) {
    return null;
  }
  return candidates[0];
}

/** Полоса, стоящая ровно над этой (её низ = наш верх). */
export function neighbourAbove(bands: Array<Band>, band: Band): Band | null {
  if (band.to === null) {
    return null;
  }
  const candidates = bands.filter((other) => other.from === band.to);
  if (candidates.length !== 1) {
    return null;
  }
  return candidates[0];
}

/**
 * Полоса лежит в непрерывной цепочке: у неё ровно один сосед снизу (или она сама
 * начинается с нуля) и ровно один сверху (или она без верха), и никто её не перекрывает.
 * Границу можно двигать только у таких — у перекрывающихся «сдвинуть границу» не имеет
 * однозначного смысла.
 */
export function isChained(bands: Array<Band>, band: Band): boolean {
  const key = bandKey(band);
  const overlapped = bands.some((other) => {
    if (bandKey(other) === key) {
      return false;
    }
    return other.from < upperBound(band) && band.from < upperBound(other);
  });
  if (overlapped) {
    return false;
  }
  const belowOk = band.from === 0 || neighbourBelow(bands, band) !== null;
  const aboveOk = band.to === null || neighbourAbove(bands, band) !== null;

  return belowOk && aboveOk;
}

export interface BandEdit {
  /** Полосы после правки, в том же порядке «низ вверх». */
  bands: Array<Band>;
  /** Полосы, появившиеся в результате правки (им нужны новые пустые цены). */
  added: Array<Band>;
  /** Полосы, исчезнувшие в результате правки (их цены больше не нужны). */
  removed: Array<Band>;
  /** Полосы, у которых сменился интервал: старый ключ → новая полоса (цены переносятся). */
  moved: Array<{ from: Band; to: Band }>;
}

function emptyEdit(bands: Array<Band>): BandEdit {
  return {
    bands: sortBandsAscending(bands), added: [], removed: [], moved: [],
  };
}

/**
 * Разбить полосу под указанной новой границей. Повторяет прежнее поведение «плюса»:
 * щёлкнули по строке — новая граница появляется между ней и той, что ниже, и делит
 * нижнюю полосу пополам. Разница в том, что верхняя граница теперь пишется явно, а не
 * восстанавливается по соседу при сохранении.
 */
export function splitAt(bands: Array<Band>, boundary: number): BandEdit {
  const target = bands.find((band) => band.from < boundary && boundary < upperBound(band));
  if (!target) {
    return emptyEdit(bands);
  }
  const lower: Band = { from: target.from, to: boundary };
  const upper: Band = { from: boundary, to: target.to };
  const rest = bands.filter((band) => bandKey(band) !== bandKey(target));

  return {
    bands: sortBandsAscending([...rest, lower, upper]),
    // Нижняя часть остаётся «той же» полосой и сохраняет свои цены, верхняя — новая.
    added: [upper],
    removed: [],
    moved: [{ from: target, to: lower }],
  };
}

/**
 * Убрать полосу. У полосы из непрерывной цепочки её диапазон забирает сосед снизу (или,
 * если убрали самую нижнюю, — сосед сверху), иначе в покрытии появилась бы дырка и в
 * закрытие ушёл бы ноль. Перекрывающая полоса просто исчезает: её диапазон и так покрыт.
 */
export function removeBand(bands: Array<Band>, removed: Band): BandEdit {
  const key = bandKey(removed);
  const rest = bands.filter((band) => bandKey(band) !== key);
  if (!rest.length) {
    return {
      bands: [], added: [], removed: [removed], moved: [],
    };
  }

  if (!isChained(bands, removed)) {
    return {
      bands: sortBandsAscending(rest), added: [], removed: [removed], moved: [],
    };
  }

  const below = neighbourBelow(bands, removed);
  if (below) {
    const grown: Band = { from: below.from, to: removed.to };
    return {
      bands: sortBandsAscending([
        ...rest.filter((band) => bandKey(band) !== bandKey(below)),
        grown,
      ]),
      added: [],
      removed: [removed],
      moved: [{ from: below, to: grown }],
    };
  }

  const above = neighbourAbove(bands, removed);
  if (above) {
    const grown: Band = { from: removed.from, to: above.to };
    return {
      bands: sortBandsAscending([
        ...rest.filter((band) => bandKey(band) !== bandKey(above)),
        grown,
      ]),
      added: [],
      removed: [removed],
      moved: [{ from: above, to: grown }],
    };
  }

  return {
    bands: sortBandsAscending(rest), added: [], removed: [removed], moved: [],
  };
}

/**
 * Сдвинуть нижнюю границу полосы. Сосед снизу тянется за ней, чтобы покрытие осталось
 * непрерывным. У самой нижней полосы сдвиг вверх открывал бы дырку `[0, новый низ)` —
 * вместо неё появляется полоса-затычка с теми же ценами: ровно то, что прежний
 * `deconstructRateTable` делал молча (строки 371–417), только теперь это видно на экране.
 */
export function moveLowerBound(bands: Array<Band>, band: Band, boundary: number): BandEdit {
  if (!Number.isFinite(boundary) || boundary < 0 || boundary === band.from) {
    return emptyEdit(bands);
  }
  if (boundary >= upperBound(band)) {
    return emptyEdit(bands);
  }
  const below = neighbourBelow(bands, band);
  const moved: Band = { from: boundary, to: band.to };
  const rest = bands.filter((other) => bandKey(other) !== bandKey(band));

  if (below) {
    if (boundary <= below.from) {
      return emptyEdit(bands);
    }
    const shrunk: Band = { from: below.from, to: boundary };
    return {
      bands: sortBandsAscending([
        ...rest.filter((other) => bandKey(other) !== bandKey(below)),
        shrunk,
        moved,
      ]),
      added: [],
      removed: [],
      moved: [{ from: below, to: shrunk }, { from: band, to: moved }],
    };
  }

  if (band.from === 0 && boundary > 0) {
    const filler: Band = { from: 0, to: boundary };
    return {
      bands: sortBandsAscending([...rest, filler, moved]),
      // Затычка повторяет цены прежней нижней полосы — как это делало сохранение.
      added: [],
      removed: [],
      moved: [{ from: band, to: moved }, { from: band, to: filler }],
    };
  }

  return {
    bands: sortBandsAscending([...rest, moved]),
    added: [],
    removed: [],
    moved: [{ from: band, to: moved }],
  };
}

/** Вердикты по ключу интервала — так они находятся и после локальных правок цен. */
export function verdictsByBandKey(verdicts: Array<BandVerdict> | null | undefined): Record<string, BandVerdict> {
  const map: Record<string, BandVerdict> = {};
  (verdicts || []).forEach((verdict) => {
    map[bandKey({ from: verdict.from, to: verdict.to })] = verdict;
  });
  return map;
}

export interface BandProbe {
  band: Band | null;
  /** Полосы, которые содержат процент, но проигрывают выбранной. */
  shadowed: Array<Band>;
}

/**
 * Какую полосу расчёт возьмёт при этом проценте. Ответ берётся из уже загруженных
 * поддиапазонов вердикта (`ranges`) — второй запрос и вторая реализация правила выбора
 * фронту не нужны, а гадать по геометрии нельзя: привязка к поставщикам решает исход.
 */
export function probeBand(
  bands: Array<Band>,
  verdicts: Record<string, BandVerdict>,
  percent: number,
): BandProbe {
  const candidates = bandsAt(bands, percent);
  let winner: Band | null = null;
  const shadowed: Array<Band> = [];

  candidates.forEach((band) => {
    const verdict = verdicts[bandKey(band)];
    if (verdict && rangesContain(verdict.ranges, percent)) {
      winner = band;
    } else {
      shadowed.push(band);
    }
  });

  return { band: winner, shadowed };
}
